/* eslint no-await-in-loop: "off" */
import React, { useState } from "react";
import { CircularProgress, Input } from "@nextui-org/react";
import { IoChevronDown, IoSearch } from "react-icons/io5";
import { useSelector } from "react-redux";
import { MdMailOutline, MdOutlineSimCardDownload } from "react-icons/md";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { SlOrganization } from "react-icons/sl";

import { GradeSliderDropdown } from "../../components/Navbar";
import CalendarDropdown from "../../components/CalendarDropdown";
import CallDurationDropdown from "../../components/CallDurationDropdown";

import { SubjectSheetIcon, UserGroupIcon } from "../../assets/icons/audio-icons";
import { transformFromUTC, transformFromUTCFromISO } from "../../utils/dateUtils";
import { useAppContext } from "../../contexts/AppContext";
import { downloadCallAudioBlob } from "../../utils/downloadCallAudio";
import MHoverToggleMenu from "../../components/Kit/MHoverToggleMenu";
import getConfig from "../../conf/config";

const LiveContentSearchbar = ({
  onTogglePageFilter,
  setChoiceFilterParam,
  pageFilters,
  organisations,
  company_config,
  gradeValue,
  setGradeValue,
  operators,
  callDurationRange,
  setCallDurationRange,
}) => {
  const {
    // debouncedSearchText: searchText,
    searchText,
    setSearchText,
    handleOnGradeChange,
    callsDateRange,
    setCallsDateRange,
    individualGrades,
  } = useAppContext();
  const [isExportLoading, setIsExportLoading] = useState(false);
  const { calls, selectedCalls = [] } = useSelector((state) => state.calls.calls);
  const { t } = useTranslation();

  const incoming_call_purposes = company_config?.incoming_call_purposes || [];
  const outgoing_call_purposes = company_config?.outgoing_call_purposes || [];

  const call_purposes = [...new Set([...incoming_call_purposes, ...outgoing_call_purposes])];

  const pageFilterToggled = pageFilters
    .filter((item) => item?.params?.length > 0)
    .map((item) => item.type);
  const nbSelectedCalls = selectedCalls.length;
  const hasSelectedCalls = nbSelectedCalls > 0;

  const formatMultiCallFileName = (fileExtension) => {
    const dateBloc = new Date()
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `export_${dateBloc}.${fileExtension}`;
  };

  const downloadAudioFile = async (callId) => downloadCallAudioBlob(callId);

  const formatAudioFileName = (item) => {
    const dateBloc = new Date(item.call_timestamp * 1000)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `${item?.operator_name.toUpperCase()}_${dateBloc}.wav`;
  };

  const exportAudioFilesZipped = async () => {
    if (!selectedCalls || selectedCalls.length <= 0) {
      return;
    }
    if (isExportLoading) {
      return;
    }
    const zipFilename = formatMultiCallFileName("zip");
    const zip = new JSZip();
    let cpt = 0;
    setIsExportLoading(true);
    try {
      while (cpt < selectedCalls.length) {
        const selectedCall = selectedCalls[cpt];
        const filename = formatAudioFileName(selectedCall);
        const audioBlob = await downloadAudioFile(selectedCall.id);
        zip.file(filename, audioBlob, { binary: true });
        cpt++;
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, zipFilename);
    } catch (error) {
      console.error("Failed to download audio files:", error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleOnExport = () => {
    // const filename = `calls_${format(new Date(dateRangeToUse.startDate), "MM-dd-yyyy")}_${format(
    //   new Date(dateRangeToUse.endDate),
    //   "MM-dd-yyyy"
    // )}.csv`;
    // parseArrayToCsv(hasSelectedCalls ? selectedCalls : calls, filename);
    exportAudioFilesZipped();
  };

  const onShareSelected = () => {
    if (!hasSelectedCalls) {
      return;
    }

    const baseUrl = getConfig().BASE_URL;

    const links = selectedCalls.map((selectedCall) => {
      return `- ${baseUrl}/calls/${selectedCall?.id}`;
    });

    const mailString = links.join("\n");

    const mailtoLink = `mailto:?subject=Partage de Calls Audio&body=${encodeURIComponent(
      `Voici les calls :
${mailString}`
    )}`;
    window.open(mailtoLink, "_blank");
  };

  const renderChoiceFilter = (quickFilter) => {
    const isSelected = pageFilterToggled.includes(quickFilter?.key);
    const htmlKey = `f-${quickFilter.key}`;
    const filterData = isSelected
      ? pageFilters.find((item) => item.type === quickFilter?.key)
      : { params: [] };

    const valueToFilter = quickFilter?.valueToFilter;

    return (
      <MHoverToggleMenu
        key={htmlKey}
        triggerComponent={
          <div
            className="cursor-pointer flex items-center rounded-md px-[12px] py-[8px] gap-[8px]"
            // onClick={() => onTogglePageFilter(quickFilter.key, valueToFilter)}
            // onKeyDown={() => onTogglePageFilter(quickFilter.key, valueToFilter)}
          >
            <quickFilter.icon
              size={16}
              color={isSelected ? "#4318FF" : "#737373"}
            />
            <span className=" text-sm whitespace-nowrap">{t(quickFilter.label)}</span>
            <IoChevronDown
              size={16}
              color={isSelected ? "#4318FF" : "#737373"}
            />
          </div>
        }
        // preventToggle={isSelected}
        items={quickFilter.choices.sort((a, b) => (a.label > b.label ? 1 : -1))}
        isMultiSelect
        selectedIds={filterData?.params || []}
        setSelectedId={setChoiceFilterParam(quickFilter.key)}
      />
    );
  };

  const renderAction = (actionLabel) => {
    switch (actionLabel) {
      case "grade":
        return (
          <GradeSliderDropdown
            label="Grade"
            sliderLabel="Select grade"
            value={gradeValue}
            onChange={(gradeValues) => {
              setGradeValue(gradeValues);
              handleOnGradeChange("individual", {
                min_individual_grade: gradeValues[0],
                max_individual_grade: gradeValues[1],
                exclude_na: !!individualGrades?.exclude_na,
              });
            }}
            excludeNA={!!individualGrades?.exclude_na}
            onExcludeNAChange={(newExcludeNA) => {
              handleOnGradeChange("individual", {
                min_individual_grade: gradeValue[0],
                max_individual_grade: gradeValue[1],
                exclude_na: newExcludeNA,
              });
            }}
          />
        );
      case "dates":
        return (
          <CalendarDropdown
            dateRangeValue={callsDateRange}
            onDateRangeChange={(dates) => {
              setCallsDateRange(dates);
            }}
            triggerClassName="bg-transparent"
          />
        );
      case "share":
        return (
          <div
            onClick={onShareSelected}
            onKeyDown={onShareSelected}
            className={`${
              hasSelectedCalls
                ? "cursor-pointer hover:bg-black/10 duration-400"
                : "cursor-not-allowed"
            } px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]/[0.6]`}
          >
            {hasSelectedCalls ? `${t("Share")} (${nbSelectedCalls})` : t("Share")}
            <MdMailOutline
              size={20}
              className="ml-2"
            />
          </div>
        );
      case "departments":
        return renderChoiceFilter({
          label: "Departments",
          key: "department",
          icon: UserGroupIcon,
          isChoiceFilter: true,
          choices: (organisations?.departments || [])?.map((department) => ({
            label: department.name,
            key: department.id,
          })),
        });
      case "operators": {
        // Deactivated operators are not offered, unless already selected so
        // they can still be unticked from the menu.
        const selectedOperatorIds =
          pageFilters.find((item) => item.type === "operators")?.params || [];

        return renderChoiceFilter({
          label: "Opérateurs",
          key: "operators",
          icon: UserGroupIcon,
          isChoiceFilter: true,
          choices: (operators || [])
            .filter(
              (operator) =>
                operator?.is_active !== false || selectedOperatorIds.includes(operator?.id)
            )
            .map((operator) => ({
              label: `${operator.first_name || ""} ${operator.last_name || ""}`.trim(),
              key: operator.id,
            })),
        });
      }
      case "call_duration":
        return (
          <CallDurationDropdown
            value={callDurationRange}
            onChange={setCallDurationRange}
          />
        );
      case "export":
        return (
          <div
            onClick={handleOnExport}
            onKeyDown={handleOnExport}
            className={`${
              hasSelectedCalls
                ? "cursor-pointer hover:bg-black/10 duration-400"
                : "cursor-not-allowed"
            } px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]/[0.6]`}
          >
            {isExportLoading && (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                className="mr-[16px]"
              />
            )}
            {hasSelectedCalls ? `${t("Download")} (${nbSelectedCalls})` : t("Download")}
            <MdOutlineSimCardDownload
              size={20}
              color="#737373"
              className="ml-2"
            />
          </div>
        );
      default:
        break;
    }
  };

  const getIsActionSelected = (action) => {
    switch (action) {
      case "dates":
        return true;
      case "grade":
        return gradeValue[0] !== 0 || gradeValue[1] !== 10;
      case "departments":
        return pageFilterToggled.includes("department");
      case "operators":
        return pageFilterToggled.includes("operators");
      case "call_duration":
        return pageFilterToggled.includes("call_duration");
      default:
        return false;
    }
  };

  const renderActions = () => {
    const actions = [
      "grade",
      "departments",
      "operators",
      "call_duration",
      "dates",
      "share",
      "export",
    ];

    return (
      <div className="flex items-center gap-[8px]">
        {actions.map((action, actionIndex) => {
          const isSelected = getIsActionSelected(action);
          return (
            <div
              key={`a-${actionIndex}`}
              className={`rounded-md ${
                isSelected
                  ? "bg-[#4318ff]/[0.18] text-primary"
                  : "border-1 border-solid border-[#E5E5E5] text-[#404040]/[0.6] bg-white hover:bg-[#F5F5F5]"
              }`}
            >
              {renderAction(action)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex w-full items-center p-1.5 px-2 mt-[16px] bg-white rounded-md">
      <Input
        placeholder="Search"
        onClear={() => setSearchText("")}
        radius="full"
        value={searchText}
        startContent={
          <IoSearch
            color="#737373"
            className="w-4 h-4"
          />
        }
        className="flex-1 min-w-[250px] grow-1"
        classNames={{
          inputWrapper: ["bg-white border-none rounded-md shadow-none"],
        }}
        onChange={(e) => setSearchText(e.target.value)}
      />
      {renderActions()}
    </div>
  );
};

export default LiveContentSearchbar;
